import { useEffect, useRef } from 'react';
import type { Match, GameState, GameAction } from '../types';

interface GameCanvasProps {
  match: Match;
  gameState: GameState | null;
  onGameAction: (action: GameAction) => void;
}

export default function GameCanvas({ match, gameState, onGameAction }: GameCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    // Initialize PixiJS application
    const canvas = canvasRef.current;
    if (!canvas) return;

    // For now, we'll use a simple HTML5 canvas
    // In a full implementation, you would initialize PixiJS here
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    // Draw game board
    const drawGameBoard = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw background
      ctx.fillStyle = '#2d5016'; // Green felt color
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw table border
      ctx.strokeStyle = '#8b4513';
      ctx.lineWidth = 8;
      ctx.strokeRect(20, 20, canvas.width - 40, canvas.height - 40);
      
      // Draw center area for cards
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      
      // Draw deck pile
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(centerX - 60, centerY - 40, 40, 60);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.strokeRect(centerX - 60, centerY - 40, 40, 60);
      
      // Draw discard pile
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(centerX + 20, centerY - 40, 40, 60);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.strokeRect(centerX + 20, centerY - 40, 40, 60);
      
      // Draw player areas
      const playerCount = match.players.length;
      const angleStep = (2 * Math.PI) / playerCount;
      
      match.players.forEach((player, index) => {
        const angle = index * angleStep;
        const radius = Math.min(canvas.width, canvas.height) * 0.3;
        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;
        
        // Draw player area
        ctx.fillStyle = '#f0f0f0';
        ctx.fillRect(x - 50, y - 30, 100, 60);
        ctx.strokeStyle = '#666666';
        ctx.lineWidth = 2;
        ctx.strokeRect(x - 50, y - 30, 100, 60);
        
        // Draw player name
        ctx.fillStyle = '#000000';
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(player.username, x, y - 10);
        
        // Draw hand count
        const handCount = gameState?.players[player.id]?.hand.length || 0;
        ctx.fillText(`${handCount} cards`, x, y + 10);
      });
    };

    drawGameBoard();

    // Handle canvas resize
    const handleResize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      drawGameBoard();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [match, gameState]);

  return (
    <div className="h-full w-full">
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{ display: 'block' }}
      />
    </div>
  );
}
